import * as React from 'react';
import { useState, useEffect, useCallback } from 'react';
import {
  Dialog, AppBar, Toolbar, IconButton, Typography, List, ListItemButton,
  ListItemText, Divider, Button, Checkbox, DialogContent, DialogActions
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import './DownloadCartDialog.css';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: 0,
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(2),
  },
}));

function DownloadCartDialog({ open, onClose, activeTheme, apiCall, badgeContent, setBadgeContent }) {
  const [downloadFiles, setDownloadFiles] = useState([]);
  const [listDownloadFiles, setListDownloadFiles] = useState([]);

  useEffect(() => {
    const stored = sessionStorage.getItem('downloadFiles');
    const files = stored ? JSON.parse(stored) : [];
    setListDownloadFiles(files);
  }, [open]);

  useEffect(() => {
    setBadgeContent(listDownloadFiles.length);
  }, [listDownloadFiles, setBadgeContent]);

  const handleDownloadInNewTab = useCallback(
    async (selectedFiles, showSnackbar) => {
      try {
        const response = await apiCall('post', 'api/generate-download-token/', {
          files: selectedFiles,
        });

        const token = response?.data?.token;
        if (!token) throw new Error('No token');

        window.open(`/download?sessionToken=${token}`, '_blank');
      } catch (error) {
        console.error('Download error:', error);
        showSnackbar?.('Error preparing download', 'error');
      }
    },
    [apiCall]
  );

  const downloadSelectedFiles = async (flag) => {
    if (flag === 'all') {
      await handleDownloadInNewTab(listDownloadFiles.map(f => f.location));
      clearAll();
    } else if (flag === 'clear') {
      clearAll();
    } else if (flag === 'remove') {
      const updated = listDownloadFiles.filter(
        f => !downloadFiles.some(df => df.name === f.name)
      );
      updateList(updated);
    } else {
      await handleDownloadInNewTab(downloadFiles.map(f => f.location));
      const updated = listDownloadFiles.filter(
        f => !downloadFiles.some(df => df.name === f.name)
      );
      updateList(updated);
    }
  };

  const clearAll = () => {
    setListDownloadFiles([]);
    setDownloadFiles([]);
    sessionStorage.setItem('downloadFiles', '[]');
  };

  const updateList = (newList) => {
    setListDownloadFiles(newList);
    sessionStorage.setItem('downloadFiles', JSON.stringify(newList));
    setDownloadFiles([]);
  };

  return (
    <BootstrapDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      className="download-dialog"
      PaperProps={{
        sx: {
          backgroundColor: activeTheme.palette.background.paper,
          color: activeTheme.palette.text.primary,
          height: '80vh',
        },
      }}
    >
      <AppBar position="relative" sx={{ bgcolor: activeTheme.palette.mode === 'dark' ? '#1565c0' : activeTheme.palette.primary.main }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Download Files ({listDownloadFiles.length})
          </Typography>
        </Toolbar>
      </AppBar>

      <DialogContent dividers sx={{ p: 0 }}>
        <List className="download-list" sx={{ bgcolor: 'background.paper' }}>
          {listDownloadFiles.length === 0 ? (
            <Typography className="empty-list-text" sx={{ color: 'text.secondary' }}>
              No files added for download.
            </Typography>
          ) : (
            listDownloadFiles.map((file, index) => (
              <React.Fragment key={file.name || index}>
                <ListItemButton
                  className="list-item-button"
                  onClick={() => {
                    setDownloadFiles(prev => {
                      const exists = prev.some(f => f.name === file.name);
                      if (exists) {
                        return prev.filter(f => f.name !== file.name);
                      }
                      return [...prev, { name: file.name, location: file.location }];
                    });
                  }}
                >
                  <Checkbox
                    edge="start"
                    checked={!!downloadFiles.find(f => f.name === file.name)}
                    tabIndex={-1}
                    disableRipple
                  />
                  <ListItemText
                    primary={file.name}
                    secondary={file.location}
                    primaryTypographyProps={{ color: 'text.primary' }}
                    secondaryTypographyProps={{ color: 'text.secondary' }}
                  />
                </ListItemButton>
                {index < listDownloadFiles.length - 1 && <Divider />}
              </React.Fragment>
            ))
          )}
        </List>
      </DialogContent>

      <DialogActions sx={{ p: 2 }} className="download-toolbar">
        <Button
          disabled={downloadFiles.length === 0}
          onClick={() => downloadSelectedFiles('remove')}
          color="error"
          variant="outlined"              
        >
          Remove ({downloadFiles.length})
        </Button>

        <Button
          disabled={listDownloadFiles.length === 0}
          onClick={() => downloadSelectedFiles('clear')}
          color="error"
          variant="outlined"
        >
          Clear Cart
        </Button>

        <Button
          disabled={downloadFiles.length === 0}
          onClick={() => downloadSelectedFiles('')}
          startIcon={<DownloadIcon />}
          color="primary"
          variant="contained"
        >
          Selected
        </Button>

        <Button
          disabled={listDownloadFiles.length === 0}
          onClick={() => downloadSelectedFiles('all')}
          startIcon={<DownloadIcon />}
          color="primary"
          variant="contained"
        >
          All
        </Button>
      </DialogActions>
    </BootstrapDialog>
  );
}

export default DownloadCartDialog;